/**
 * Narrow classroom roster self-service — /classrooms/roster
 * Move / add children with effective date. Tablet-first (Gan Ezra / Devorah).
 * No classroom CRUD, no student editing, no discharge.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../AppHeader';
import SessionSelector from '../SessionSelector';
import { useAuth } from '../../contexts/AuthContext';
import { rosterAPI, sessionAPI } from '../../services/api';

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function RosterManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [students, setStudents] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [moveStudent, setMoveStudent] = useState(null);
  const [targetClassroomId, setTargetClassroomId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [capacityWarning, setCapacityWarning] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([sessionAPI.getSessions(), sessionAPI.getCurrentSession()])
      .then(([sessRes, curRes]) => {
        const list = Array.isArray(sessRes.data) ? sessRes.data : (sessRes.data?.results || []);
        setSessions(list);
        if (curRes.data?.id) setCurrentSession(curRes.data);
        else if (list.length) setCurrentSession(list.find((s) => s.is_active) || list[0]);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!currentSession?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [clsRes, unRes, pendRes] = await Promise.all([
        rosterAPI.classrooms(currentSession.id),
        rosterAPI.unassigned(currentSession.id),
        rosterAPI.pending(currentSession.id),
      ]);
      const cls = Array.isArray(clsRes.data) ? clsRes.data : [];
      setClassrooms(cls);
      setUnassigned(Array.isArray(unRes.data) ? unRes.data : []);
      setPending(Array.isArray(pendRes.data) ? pendRes.data : []);
      if (selectedId && !cls.find((c) => c.id === selectedId)) {
        setSelectedId(cls[0]?.id || null);
      } else if (!selectedId && cls.length) {
        setSelectedId(cls[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load rosters');
    } finally {
      setLoading(false);
    }
  }, [currentSession?.id, selectedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      setStudents([]);
      return;
    }
    rosterAPI.students(selectedId)
      .then((res) => setStudents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStudents([]));
  }, [selectedId]);

  const selected = classrooms.find((c) => c.id === selectedId) || null;

  const openMove = (student, fromUnassigned = false) => {
    setMoveStudent({ ...student, fromUnassigned });
    setTargetClassroomId(fromUnassigned ? (selectedId || '') : '');
    setEffectiveDate(todayISO());
    setCapacityWarning(null);
  };

  const doAssign = async (override = false) => {
    if (!moveStudent || !targetClassroomId) return;
    setSaving(true);
    setError(null);
    try {
      await rosterAPI.assign({
        student: moveStudent.id,
        classroom: Number(targetClassroomId),
        effective_date: effectiveDate,
        override_capacity: override,
      });
      setMoveStudent(null);
      setCapacityWarning(null);
      await load();
      if (selectedId) {
        const res = await rosterAPI.students(selectedId);
        setStudents(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      const data = e.response?.data;
      if (e.response?.status === 409 || data?.error === 'capacity_exceeded' || data?.can_override) {
        setCapacityWarning(data?.warning || data);
      } else {
        setError(data?.detail || data?.message || e.message || 'Move failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelPending = async (id) => {
    try {
      await rosterAPI.cancelPending(id);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Cancel failed');
    }
  };

  return (
    <Box sx={{ pb: 10, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <AppHeader backTo="/" />
      <Container maxWidth="sm" sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ minHeight: 44, textTransform: 'none' }}
          >
            Home
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
            Class rosters
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Move or add children. Signed in as {user?.email} ({user?.role}).
        </Typography>

        <SessionSelector
          sessions={sessions}
          currentSession={currentSession}
          onSessionChange={(id) => {
            const next = sessions.find((s) => String(s.id) === String(id));
            setCurrentSession(next || null);
            setSelectedId(null);
          }}
        />

        {error && (
          <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel id="roster-class-label">Class</InputLabel>
              <Select
                labelId="roster-class-label"
                label="Class"
                value={selectedId || ''}
                onChange={(e) => setSelectedId(e.target.value)}
                sx={{ minHeight: 52, fontSize: '1.1rem' }}
              >
                {classrooms.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ minHeight: 48, fontSize: '1.05rem' }}>
                    {c.class_num}
                    {c.name ? ` — ${c.name}` : ''}
                    {c.teacher ? ` (${c.teacher})` : ''}
                    {` · ${c.enrollment_count}/${c.licensed_max}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selected && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {selected.label} — {students.length} children
              </Typography>
            )}

            <List disablePadding>
              {students.map((s) => (
                <ListItemButton
                  key={s.id}
                  onClick={() => openMove(s)}
                  sx={{
                    bgcolor: '#fff',
                    mb: 1,
                    borderRadius: 2,
                    minHeight: 64,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  }}
                >
                  <ListItemText
                    primary={s.name}
                    secondary={`Class ${s.class_num} · tap to move`}
                    primaryTypographyProps={{ fontSize: '1.15rem', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItemButton>
              ))}
              {!students.length && (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No children in this class.
                </Typography>
              )}
            </List>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
              Unassigned ({unassigned.length})
            </Typography>
            <List disablePadding>
              {unassigned.map((s) => (
                <ListItemButton
                  key={s.id}
                  onClick={() => openMove(s, true)}
                  sx={{
                    bgcolor: '#fff8e1',
                    mb: 1,
                    borderRadius: 2,
                    minHeight: 56,
                  }}
                >
                  <ListItemText
                    primary={s.name}
                    secondary={s.class_num ? `stale class_num ${s.class_num}` : 'No class'}
                    primaryTypographyProps={{ fontSize: '1.1rem', fontWeight: 600 }}
                  />
                </ListItemButton>
              ))}
            </List>

            {pending.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                  Scheduled moves
                </Typography>
                {pending.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      bgcolor: '#e3f2fd',
                      p: 2,
                      borderRadius: 2,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600}>{p.student_name}</Typography>
                      <Typography variant="body2">
                        {p.from_class_num || '—'} → {p.to_class_num} on {p.effective_date}
                      </Typography>
                    </Box>
                    <Button
                      size="large"
                      onClick={() => cancelPending(p.id)}
                      sx={{ minHeight: 44 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ))}
              </>
            )}
          </>
        )}
      </Container>

      <Dialog open={Boolean(moveStudent)} onClose={() => !saving && setMoveStudent(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: '1.25rem' }}>
          {moveStudent?.fromUnassigned ? 'Add to class' : 'Move child'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 600 }}>
            {moveStudent?.name}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>To class</InputLabel>
            <Select
              label="To class"
              value={targetClassroomId}
              onChange={(e) => {
                setTargetClassroomId(e.target.value);
                setCapacityWarning(null);
              }}
              sx={{ minHeight: 52 }}
            >
              {classrooms
                .filter((c) => c.id !== selectedId || moveStudent?.fromUnassigned)
                .map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ minHeight: 48 }}>
                    {c.class_num}{c.name ? ` — ${c.name}` : ''} · {c.enrollment_count}/{c.licensed_max}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="date"
            label="Effective date"
            InputLabelProps={{ shrink: true }}
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            sx={{ mb: 2, '& .MuiInputBase-root': { minHeight: 52 } }}
            helperText="Today applies now. A future date waits (child stays in current class until then)."
          />
          {capacityWarning && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {capacityWarning.message || 'This class is over licensed capacity.'}
              {' '}You can proceed — the overbook will be recorded.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setMoveStudent(null)} disabled={saving} sx={{ minHeight: 48 }}>
            Cancel
          </Button>
          {capacityWarning ? (
            <Button
              variant="contained"
              color="warning"
              disabled={saving || !targetClassroomId}
              onClick={() => doAssign(true)}
              sx={{ minHeight: 48, flex: 1 }}
            >
              Proceed anyway
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={saving || !targetClassroomId}
              onClick={() => doAssign(false)}
              sx={{ minHeight: 48, flex: 1 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
